import { useConfig } from '../../../eliot-components/src/ConfigProvider/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useManageContext } from '../../../contexts/ManageContext/ManageContext';
import { useHistory } from 'react-router-dom';
import { fetchWithAuth } from '../../../fetch';
import { Operations } from './components/SearchInput/SearchInput';

const parseFilter = (filter: Filter): any[] => {
  if (filter[Operations.IN]) {
    return filter.in ? filter.in.map((f) => f.trim()) : [];
  } else if (filter[Operations.EQUAL]) {
    return [filter[Operations.EQUAL]?.trim()];
  }

  return [];
};

const useDeviceSearch = (query?: string[], advancedSearchFilters?: Filters) => {
  const { apiBaseURL } = useConfig();
  const { state, dispatch } = useManageContext();
  const [isLoading, setIsLoading] = useState(false);

  const calcQuery = useMemo(() => {
    return query ? query : [];
  }, [query]);

  useEffect(() => {
    const fetch = async (queryFilters: any) => {
      var serviceUrl = `${apiBaseURL}/v1/devices?`;

      let queryMap = new Map();
      for (let q of calcQuery) {
        queryMap.set(q.toLowerCase(), '');
        queryMap.set(q.toUpperCase(), '');
        queryMap.set(q, '');
      }

      queryMap.forEach((value: string, key: string) => {
        if (key.includes(':')) {
          serviceUrl = serviceUrl + `MAC=` + encodeURIComponent(key) + `&`;
        } else {
          serviceUrl =
            serviceUrl +
            `serial_number_or_imei=` +
            encodeURIComponent(key) +
            `&`;
        }
      });

      Object.keys(queryFilters).forEach((key: string) => {
        if (Array.isArray(queryFilters[key])) {
          queryFilters[key].forEach((value: any) => {
            serviceUrl = serviceUrl + `${key}=${encodeURIComponent(value)}&`;
          });
        } else {
          serviceUrl =
            serviceUrl + `${key}=${encodeURIComponent(queryFilters[key])}&`;
        }
      });

      // include archived devices
      serviceUrl += 'archived=true';
      // include stock devices
      serviceUrl += '&instock=true';
      const init = {
        headers: { Accept: 'application/json' },
      };

      setIsLoading(true);
      try {
        // TODO: look at this one
        const response = await fetchWithAuth(serviceUrl, init);
        const result: any = await response.json();

        dispatch({
          type: 'device-list-changed',
          data: result,
        });
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        window.location.replace(`/error?error=${'Error Loading Devices'}`);
        dispatch({
          type: 'device-list-changed',
          data: [],
        });
      }
    };

    const queryFilters: any = {};
    if (advancedSearchFilters) {
      if (advancedSearchFilters.serialNumber) {
        const filter = advancedSearchFilters.serialNumber;
        // @ts-ignore
        if (filter[Operations.LIKE] && filter[Operations.LIKE].length > 3) {
          queryFilters['serial_number_or_imei'] =
            filter[Operations.LIKE]?.trim();
        } else {
          queryFilters['serial_number'] = parseFilter(filter).filter(
            (f) => f.length > 3
          );
        }
      }
      if (advancedSearchFilters.imei) {
        const filter = advancedSearchFilters.imei;
        if (filter[Operations.LIKE]) {
          queryFilters['serial_number_or_imei'] =
            filter[Operations.LIKE]?.trim();
        } else {
          queryFilters['imei'] = parseFilter(filter).filter(
            (f) => f.length > 3
          );
        }
      }

      if (advancedSearchFilters.model) {
        queryFilters['model'] = parseFilter(advancedSearchFilters.model);
      }

      if (advancedSearchFilters.measurementCount) {
        const filter = advancedSearchFilters.measurementCount;
        if (filter[Operations.LESSTHEN]) {
          queryFilters['measurementLessThan'] =
            filter[Operations.LESSTHEN]?.trim();
        }

        if (filter[Operations.GREATERTHEN]) {
          queryFilters['measurementGreaterThan'] =
            filter[Operations.GREATERTHEN]?.trim();
        }

        if (filter[Operations.GREATEREQUALTHEN]) {
          try {
            queryFilters['measurementGreaterThan'] =
              parseInt(
                filter[Operations.GREATEREQUALTHEN]?.trim() as string,
                10
              ) - 1;
          } catch (e) {
            queryFilters['measurementGreaterThan'] =
              filter[Operations.GREATEREQUALTHEN]?.trim();
          }
        }

        if (filter[Operations.LESSEQUALTHEN]) {
          try {
            queryFilters['measurementLessThan'] =
              parseInt(filter[Operations.LESSEQUALTHEN]?.trim() as string, 10) +
              1;
          } catch (e) {
            queryFilters['measurementLessThan'] =
              filter[Operations.LESSEQUALTHEN]?.trim();
          }
        }
        if (filter[Operations.EQUAL]) {
          queryFilters['measurementEquals'] = filter[Operations.EQUAL]?.trim();
        }
      }
      if (advancedSearchFilters.mac) {
        const filter = advancedSearchFilters.mac;
        // @ts-ignore
        if (filter[Operations.LIKE] && filter[Operations.LIKE].length > 3) {
          queryFilters['MAC'] = filter[Operations.LIKE]?.trim();
        } else {
          queryFilters['MAC'] = parseFilter(filter).filter((f) => f.length > 3);
        }
      }
      if (advancedSearchFilters.iccid) {
        const filter = advancedSearchFilters.iccid;
        // @ts-ignore
        if (filter[Operations.LIKE] && filter[Operations.LIKE].length > 3) {
          queryFilters['iccid'] = filter[Operations.LIKE]?.trim();
        } else {
          queryFilters['iccid'] = parseFilter(filter).filter(
            (f) => f.length > 3
          );
        }
      }
    }

    let validQueryLength =
      calcQuery.length > 0 ||
      Object.keys(queryFilters).length > 0 ||
      (!!advancedSearchFilters && state.devices.length === 0);
    for (const q of calcQuery) {
      if (q.length < 3) {
        validQueryLength = false;
      }
    }
    if (validQueryLength && !isLoading) {
      fetch(queryFilters);
    } else {
      dispatch({
        type: 'device-list-changed',
        data: [],
      });
    }
  }, [calcQuery, state.lastQueryChange, advancedSearchFilters]); // eslint-disable-line react-hooks/exhaustive-deps
};

const useDeviceScan = () => {
  const { apiBaseURL } = useConfig();
  const { state, dispatch } = useManageContext();
  const { scannedQuery } = state;
  const history = useHistory();

  useEffect(() => {
    const fetch = async () => {
      var serviceUrl =
        `${apiBaseURL}/v1/devices?serial_number_or_imei=` + scannedQuery;

      const init = {
        headers: { Accept: 'application/json' },
      };

      // TODO: look at this one
      const response = await fetchWithAuth(serviceUrl, init);
      const result: any = await response.json();
      if (result.devices && result.devices.length === 1) {
        const device = result.devices[0];
        dispatch({
          type: 'reset-scanned-query',
        });

        history.push(`/devices/${encodeURIComponent(device.id)}`);
      } else {
        dispatch({
          type: 'search-started',
        });
        dispatch({
          type: 'search-query-changed',
          data: scannedQuery,
        });
      }
    };

    if (scannedQuery && scannedQuery.length >= 3) {
      fetch();
    }
  }, [scannedQuery]); // eslint-disable-line react-hooks/exhaustive-deps
};

export { useDeviceSearch, useDeviceScan };
